import React, {useState, useEffect, useRef} from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import {makeStyles, Theme, createStyles} from '@material-ui/core/styles';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import moment from 'moment';
import {CalendarNylasSource} from 'sources/calendar/CalendarNylasSource';
import {List} from 'immutable';
import {FindTimeAvailableTime, FindTimeContainerProps} from '@components/calendar/FindTime.types';

const DISPLAY_AVAILABLE_TIMES_COUNT = 5;

interface FindTimeLinkProps extends FindTimeContainerProps {
  recipientHasNylas: boolean;
}

interface FindTimeResultsProps extends FindTimeLinkProps {
  onClose: () => void;
  setShowingError: (show: boolean) => void;
}

const FindTimeResults: React.FC<FindTimeResultsProps> = ({
  coachUserId,
  recipientUserId,
  recipientFirstName,
  recipientHasNylas,
  meetingMinutes,
  searchWeeks,
  dayOfWeek,
  desiredTime,
  startDate,
  onlyOnTheHour,
  findTimeChoiceMade,
  onClose,
  setShowingError,
}) => {
  const popupClasses = usePopupStyles();

  // Used a ref to avoid re-rendering the component when the `loading` state changes
  // Learn more at https://dmitripavlutin.com/react-useeffect-infinite-loop/#12-using-a-reference
  const fetchingRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [displayedAvailableTimes, setDisplayedAvailableTimes] = useState<
    List<FindTimeAvailableTime>
  >(List());
  const [remainingAvailableTimes, setRemainingAvailableTimes] = useState<
    List<FindTimeAvailableTime>
  >(List());

  useEffect(() => {
    const fetchFindTimes = async () => {
      if (!fetchingRef.current) {
        setLoading(true);
        setError(false);
        setShowingError(false);
        const data = await CalendarNylasSource.fetchFindTimes(
          recipientHasNylas ? [coachUserId, recipientUserId] : [coachUserId],
          meetingMinutes,
          searchWeeks,
          dayOfWeek,
          desiredTime,
          startDate
        ).catch(err => {
          setError(true);
          setShowingError(true);
          if (err.response.status === 452) {
            setErrorMessage(
              "Your calendar is not connected. If you'd like to use " +
                `this feature, click the "Conncet" button on <a href="/admin/profile">your user profile </a> page.`
            );
          } else if (err.response.status === 453) {
            setErrorMessage(
              "Your calendar is no longer connected. If you'd like to use " +
                `this feature, click the "Fix it" button on <a href="/admin/profile">your user profile </a> page.`
            );
          } else if (err.response.status === 454) {
            setErrorMessage(
              'Your calendar connection is not ready yet. Please try again in a few minutes.'
            );
          } else {
            setErrorMessage('Sorry! an error occurred.');
          }
          return [];
        });

        const listData = List<string>(data);

        // Only allow quarter or whole hours as needed via
        // and "ends with" regex. God help us.
        //
        // Simple heuristics, just first 5 for now to see it work...
        //
        // Thought about grouping by day, and taking first 3
        // for first day, and second 2 from second day if more
        // than one day exists.
        //
        // Or grouping by day and hour... Lots of possibilities!
        const validMinutes = onlyOnTheHour ? /:00$/ : /:00|:15|:30|:45$/;
        const filteredAvailableTimes = listData
          .filter(x => validMinutes.test(x))
          .map(x => {
            const dayTime = x.split(' ');
            const day = dayTime[0];
            const hoursMinutes = dayTime[1].split(':');
            const hour = Number.parseInt(hoursMinutes[0], 10);
            const minutes = Number.parseInt(hoursMinutes[1], 10);

            const sampleDate = new Date(`2022-03-09T${dayTime[1]}:00Z`);
            const sampleTimeDisplay = `${day} ${sampleDate.toLocaleTimeString('en-US', {
              timeZone: 'UTC',
              hour12: true,
              hour: 'numeric',
              minute: 'numeric',
            })}`;
            return {
              // Note, hardcoded date doesn't matter in conversion from 24 to 12 time format below.
              timeDisplay: sampleTimeDisplay,
              day: day,
              hour: hour,
              minutes: minutes,
            };
          });

        setDisplayedAvailableTimes(filteredAvailableTimes.take(DISPLAY_AVAILABLE_TIMES_COUNT));
        setRemainingAvailableTimes(
          filteredAvailableTimes.skip(DISPLAY_AVAILABLE_TIMES_COUNT).toList()
        );
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchFindTimes();
  }, [
    recipientHasNylas,
    coachUserId,
    recipientUserId,
    meetingMinutes,
    searchWeeks,
    dayOfWeek,
    desiredTime,
    startDate,
    onlyOnTheHour,
    setShowingError,
  ]);

  const showMore = () => {
    setDisplayedAvailableTimes(remainingAvailableTimes.take(DISPLAY_AVAILABLE_TIMES_COUNT));
    setRemainingAvailableTimes(
      remainingAvailableTimes.skip(DISPLAY_AVAILABLE_TIMES_COUNT).toList()
    );
  };

  const availableTimeButtonClicked = (chosenTime: FindTimeAvailableTime) => {
    findTimeChoiceMade(chosenTime);
    onClose();
  };

  return (
    <div className={popupClasses.popup}>
      <div className={popupClasses.popupHeader}>
        <div className={popupClasses.popupTitle}>Find a time to meet</div>
        <CloseIcon color={'gray'} onClick={onClose} />
      </div>

      {loading && (
        <div className={popupClasses.loading}>
          <CircularProgress />
        </div>
      )}

      {!loading && !error && !displayedAvailableTimes.isEmpty() && (
        <>
          <div>
            <p className={popupClasses.popupDescription}>
              The following times are available on your{' '}
              {recipientHasNylas ? `and ${recipientFirstName}'s calendars` : 'calendar'} over the
              next {searchWeeks === 1 ? 'week' : searchWeeks + ' weeks'}.
            </p>
          </div>
          <div className={popupClasses.buttons}>
            {displayedAvailableTimes.map(x => (
              <Button
                key={x.timeDisplay}
                size="large"
                variant="outlined"
                onClick={() => availableTimeButtonClicked(x)}>
                {x.timeDisplay}
              </Button>
            ))}
          </div>
          {remainingAvailableTimes.size > 0 && (
            <div className={popupClasses.showMore}>
              <Button size="small" variant="text" onClick={showMore}>
                Show More
              </Button>
            </div>
          )}
        </>
      )}

      {!loading && !error && displayedAvailableTimes.isEmpty() && (
        <div>
          <p className={popupClasses.popupError}>There is no free time available.</p>
        </div>
      )}

      {!loading && error && (
        <p className={popupClasses.popupError} dangerouslySetInnerHTML={{__html: errorMessage}} />
      )}
    </div>
  );
};

const FindTimeLink: React.FC<FindTimeLinkProps> = ({
  coachUserId,
  recipientUserId,
  recipientFirstName = 'recipient',
  recipientHasNylas = false,
  meetingMinutes = 15,
  searchWeeks = 4,
  dayOfWeek = moment().format('dddd').toUpperCase(),
  desiredTime = '09:00',
  startDate,
  onlyOnTheHour = false,
  findTimeChoiceMade,
}) => {
  const [open, setOpen] = useState(false);
  const [showingError, setShowingError] = useState(false);

  const useStyles = makeStyles(theme => ({
    customSize: {
      maxWidth: showingError ? 260 : 275,
      minWidth: showingError ? 260 : 275,
      minHeight: showingError ? 150 : 410,
      maxHeight: showingError ? 150 : 410,
    },
  }));

  const classes = useStyles();

  return (
    <Tooltip
      placement="right"
      classes={{tooltip: classes.customSize}}
      title={
        <FindTimeResults
          coachUserId={coachUserId}
          recipientUserId={recipientUserId}
          recipientFirstName={recipientFirstName}
          recipientHasNylas={recipientHasNylas}
          meetingMinutes={meetingMinutes}
          searchWeeks={searchWeeks}
          dayOfWeek={dayOfWeek}
          desiredTime={desiredTime}
          startDate={startDate}
          onlyOnTheHour={onlyOnTheHour}
          findTimeChoiceMade={findTimeChoiceMade}
          onClose={() => setOpen(false)}
          setShowingError={setShowingError}
        />
      }
      arrow
      disableFocusListener
      disableHoverListener
      disableTouchListener
      interactive
      open={open}>
      <Button variant="text" size="medium" onClick={() => setOpen(!open)}>
        Find a time
      </Button>
    </Tooltip>
  );
};

export default FindTimeLink;

const usePopupStyles = makeStyles((theme: Theme) =>
  createStyles({
    popup: {
      padding: 15,
    },
    popupHeader: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    popupTitle: {
      fontSize: 22,
    },
    popupDescription: {
      fontSize: 16,
    },
    popupError: {
      fontSize: 14,
      paddingTop: 10,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    buttons: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
    showMore: {
      marginTop: 15,
    },
    loading: {
      marginTop: 30,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
    },
    textFields: {
      width: 40,
      padding: 20,
    },
    dataEntrySentences: {
      fontSize: 16,
      display: 'inline-flex',
      alignContent: 'center',
      alignItems: 'center',
    },
  })
);
